import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { verifyCertification } from '../lib/api';
import { Badge } from '../components/ui/badge';
import { formatDate, getStatusClass } from '../lib/utils';
import { CheckCircle2, XCircle, Clock, ExternalLink, AlertTriangle, Award } from 'lucide-react';

export default function Verify() {
  const { declarationId } = useParams();
  const [declaration, setDeclaration] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    loadDeclaration();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [declarationId]);

  const loadDeclaration = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await verifyCertification(declarationId);
      setDeclaration(response.data);
    } catch (err) {
      if (err.response?.status === 404) {
        setError('Declaration not found');
      } else {
        setError('Failed to verify declaration');
      }
    } finally {
      setLoading(false);
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'active':
        return <CheckCircle2 className="w-8 h-8 text-emerald-600" />;
      case 'expired':
        return <Clock className="w-8 h-8 text-zinc-500" />;
      case 'revoked':
      case 'withdrawn':
        return <AlertTriangle className="w-8 h-8 text-amber-500" />;
      default:
        return <AlertTriangle className="w-8 h-8 text-amber-500" />;
    }
  };

  const getStatusMessage = (status, standardsCount) => {
    switch (status) {
      case 'active':
        return `This declaration is valid and active for ${standardsCount} standard${standardsCount !== 1 ? 's' : ''}.`;
      case 'expired':
        return 'This declaration has expired.';
      case 'revoked':
      case 'withdrawn':
        return 'This declaration has been withdrawn.';
      default:
        return 'Unknown status.';
    }
  };
  
  const getStatusLabel = (status) => {
    if (status === 'revoked') return 'WITHDRAWN';
    return status.toUpperCase();
  };

  return (
    <div className="min-h-screen bg-white" data-testid="verify-page">
      {/* Header */}
      <header className="border-b border-zinc-200">
        <div className="container mx-auto px-8 py-6">
          <div className="flex items-center justify-between">
            <div>
              <Link to="/" className="header-logo text-xl">AI Accountability Standards</Link>
            </div>
            <nav className="flex items-center gap-6">
              <Link to="/registry" className="nav-link">Registry</Link>
              <Link to="/tools" className="nav-link">Tools</Link>
              <Link to="/org/login" className="text-sm text-zinc-500 hover:text-zinc-700">Organization Login</Link>
            </nav>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="container mx-auto px-8 py-12">
        <div className="max-w-2xl mx-auto">
          <div className="breadcrumb">
            <Link to="/registry" className="hover:text-zinc-700">Registry</Link>
            <span className="breadcrumb-separator">/</span>
            <span>Verify</span>
          </div>

          <h1 className="page-title text-2xl mb-8">Declaration Verification</h1>

          {loading ? (
            <div className="flex justify-center py-12">
              <div className="text-zinc-500">Verifying declaration...</div>
            </div>
          ) : error ? (
            <div className="border border-red-200 bg-red-50 p-8 text-center">
              <XCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
              <h2 className="text-xl font-semibold text-red-800 mb-2">Verification Failed</h2>
              <p className="text-red-600">{error}</p>
              <p className="text-sm text-red-500 mt-4">
                Declaration ID: <span className="font-mono">{declarationId}</span>
              </p>
            </div>
          ) : declaration ? (
            <div className="border border-zinc-200">
              {/* Status Banner */}
              <div className={`p-6 flex items-center gap-4 ${
                declaration.status === 'active' ? 'bg-emerald-50 border-b border-emerald-100' :
                (declaration.status === 'revoked' || declaration.status === 'withdrawn') ? 'bg-amber-50 border-b border-amber-100' :
                'bg-zinc-50 border-b border-zinc-100'
              }`}>
                {getStatusIcon(declaration.status)}
                <div>
                  <div className="flex items-center gap-2">
                    <Badge variant="outline" className={`rounded-none ${
                      declaration.status === 'active' ? 'bg-emerald-50 text-emerald-700 border-emerald-200' :
                      (declaration.status === 'revoked' || declaration.status === 'withdrawn') ? 'bg-amber-50 text-amber-700 border-amber-200' :
                      'bg-zinc-100 text-zinc-600 border-zinc-300'
                    }`} data-testid="cert-status">
                      {getStatusLabel(declaration.status)}
                    </Badge>
                  </div>
                  <p className={`text-sm mt-1 ${
                    declaration.status === 'active' ? 'text-emerald-700' :
                    (declaration.status === 'revoked' || declaration.status === 'withdrawn') ? 'text-amber-700' :
                    'text-zinc-600'
                  }`}>
                    {getStatusMessage(declaration.status, declaration.standards_count || 1)}
                  </p>
                </div>
              </div>

              {/* Withdrawal Info */}
              {(declaration.status === 'revoked' || declaration.status === 'withdrawn') && declaration.revocation_reason && (
                <div className="p-4 bg-amber-50 border-b border-amber-100">
                  <p className="text-sm text-amber-800">
                    <strong>Withdrawal Reason:</strong> {declaration.revocation_reason}
                  </p>
                  {declaration.revocation_date && (
                    <p className="text-sm text-amber-600 mt-1">
                      <strong>Withdrawal Date:</strong> {formatDate(declaration.revocation_date)}
                    </p>
                  )}
                </div>
              )}

              {/* Details */}
              <div className="divide-y divide-zinc-200">
                <div className="p-4 flex">
                  <span className="w-40 text-sm text-zinc-500">Declaration ID</span>
                  <span className="font-mono font-medium" data-testid="cert-id">{declaration.declaration_id}</span>
                </div>
                <div className="p-4 flex">
                  <span className="w-40 text-sm text-zinc-500">Organization</span>
                  <span className="font-medium" data-testid="cert-org">{declaration.org_name}</span>
                </div>
                
                {/* Standards - Now shows multiple */}
                <div className="p-4">
                  <span className="text-sm text-zinc-500 block mb-3">Declared Standards ({declaration.standards_count || declaration.standards?.length || 1})</span>
                  <div className="space-y-2">
                    {declaration.standards && declaration.standards.length > 0 ? (
                      declaration.standards.map((std, idx) => (
                        <div key={idx} className="flex items-center justify-between bg-zinc-50 p-3">
                          <div>
                            <span className="font-medium" data-testid={`cert-standard-${idx}`}>{std.title}</span>
                            <span className="text-xs text-zinc-400 ml-2 font-mono">({std.id})</span>
                            {std.version && (
                              <span className="text-xs text-zinc-400 ml-1 font-mono">{std.version}</span>
                            )}
                          </div>
                          {std.authority_url && (
                            <a 
                              href={std.authority_url} 
                              target="_blank" 
                              rel="noopener noreferrer"
                              className="text-zinc-600 hover:text-zinc-900 inline-flex items-center gap-1 text-sm"
                            >
                              <ExternalLink className="w-3 h-3" />
                            </a>
                          )}
                        </div>
                      ))
                    ) : (
                      <div className="bg-zinc-50 p-3">
                        <span data-testid="cert-standard">{declaration.standard_title}</span>
                        <span className="text-xs text-zinc-400 ml-2 font-mono">({declaration.standard_id})</span>
                      </div>
                    )}
                  </div>
                </div>
                
                <div className="p-4 flex">
                  <span className="w-40 text-sm text-zinc-500">Issue Date</span>
                  <span className="font-mono text-sm" data-testid="cert-issue-date">{formatDate(declaration.issue_date)}</span>
                </div>
                <div className="p-4 flex">
                  <span className="w-40 text-sm text-zinc-500">Expiry Date</span>
                  <span className="font-mono text-sm" data-testid="cert-expiry-date">{formatDate(declaration.expiry_date)}</span>
                </div>
              </div>
            </div>
          ) : null}

          {/* Disclaimer */}
          <div className="mt-8 p-4 bg-zinc-50 border border-zinc-200">
            <p className="text-xs text-zinc-500">
              <strong>Disclaimer:</strong> Accountability declaration indicates documented governance alignment with published standards. 
              It does not imply endorsement, approval, regulatory compliance, or performance guarantees.
              <Link to="/declaration-scope" className="underline ml-1 hover:text-zinc-700">Read full declaration scope</Link>.
            </p>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="border-t border-zinc-200 py-8 mt-12">
        <div className="container mx-auto px-8">
          <div className="flex items-center justify-between text-sm text-zinc-500">
            <span>AI Accountability Standards Council</span>
            <div className="flex items-center gap-6">
              <Link to="/terms" className="hover:text-zinc-700">Terms</Link>
              <Link to="/privacy" className="hover:text-zinc-700">Privacy</Link>
              <Link to="/declaration-scope" className="hover:text-zinc-700">Declaration Scope</Link>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
